// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { IMessageFormatProvider } from "@lib/messaging/IMessageFormatProvider";
import { IMessageSender } from "@lib/messaging/IMessageSender";
import { IMessageSenderFactory } from "@lib/messaging/IMessageSenderFactory";
import { MessageServerContext } from "@lib/messaging/MessageServerContext";
import { NetworkNode } from "@lib/messaging/NetworkNode";
import { NetworkSource } from "@lib/messaging/NetworkSource";
import { RawMessage } from "@lib/messaging/RawMessage";
import { RawMessageSource } from "@lib/messaging/RawMessageSource";
import { Container } from "@lib/utilities/Container";

const log = LogManager.createLog("");

/**
 * Provides methods for sending and receiving messages with a specific format.
 * Uses `IMessageFormatProvider`s to handle incoming messages.
 */
export class MessageServer implements IMessageSenderFactory
{
  readonly messageFormatProviders = new Container<IMessageFormatProvider>();
  readonly networkNode: NetworkNode;

  constructor(networkNode: NetworkNode)
  {
    this.networkNode = networkNode;

    this.networkNode.onMessage.addListener(this.handleMessage);
  }

  createMessageSender(format: string): IMessageSender
  {
    return {
      sendMessage: (destination, messageObject) =>
      {
        this.sendMessage(destination, format, messageObject);
      }
    };
  }

  sendMessage(destination: NetworkSource, messageFormat: string, messageObject: object)
  {
    const rawMessage: RawMessage = {
      destination: destination,
      messageFormat: messageFormat,
      messageObject: messageObject,
      source: NetworkSource.create(this.networkNode.networkType)
    };

    this.networkNode.sendRawMessage(rawMessage);
    log.trace("Sent message: '%s' |--> '%s' (%o)", rawMessage.source.toString(), rawMessage.destination.toString(), rawMessage);
  }

  private handleMessage = (source: RawMessageSource, rawMessage: RawMessage) =>
  {
    const context = new MessageServerContext({
      destination: rawMessage.destination,
      messageSender: this.createMessageSender(rawMessage.messageFormat),
      socket: source.socket,
      source: rawMessage.source
    });

    const messageFormatProvider = this.messageFormatProviders.get(rawMessage.messageFormat);
    if (!messageFormatProvider)
    {
      log.error("Dropped received raw message: invalid message format '%s' (%o)", rawMessage.messageFormat, rawMessage);
      log.error("Received a message with an invalid format '%s': %o", rawMessage.messageFormat, rawMessage);
      return;
    }

    messageFormatProvider.handleMessage(context, rawMessage.messageObject);
    log.trace("Handled message: '%s' -->| '%s' (%o)", rawMessage.source.toString(), rawMessage.destination.toString(), rawMessage);
  };
}
